import { Content, Root, Trigger } from '@radix-ui/react-popover';
import clsx from 'clsx';
import { useId } from 'react';
import ColorSwatch from '../ColorSwatch';
import type { ColorPickerControlProps } from './ColorPickerControl.types';

const popoverContentStyles = `
  tw:data-[state=closed]:animate-out
  tw:data-[state=open]:animate-in
  tw:relative
  tw:z-[1200]
  tw:min-w-6
  tw:rounded-[0.5rem]
  tw:border
  tw:border-solid
  tw:border-sq-disabled-gray
  tw:bg-sq-white
  tw:text-sq-text-color
  tw:outline-none
  tw:focus-visible:outline-none
  tw:data-[side=bottom]:animate-slideUpAndFade
  tw:data-[side=left]:animate-slideRightAndFade
  tw:data-[side=right]:animate-slideLeftAndFade
  tw:data-[side=top]:animate-slideDownAndFade
  tw:dark:border-gray-500
  tw:dark:bg-sq-dark-background
  tw:dark:text-sq-text-color-dark
`;

function ColorPickerControl({
  color,
  label,
  children,
  placement = 'bottom',
  align = 'start',
  extraClassNames = '',
  id,
  testId,
  onOpen,
}: ColorPickerControlProps) {
  const swatchId = useId();

  const triggerLabel = (
    <label
      className="tw:cursor-pointer tw:text-sm tw:whitespace-nowrap tw:text-sq-text-color tw:dark:text-sq-text-color-dark"
      htmlFor={swatchId}>
      {label}
    </label>
  );

  const swatch = <ColorSwatch aria-label={label} color={color} id={id ?? swatchId} size="sm" onClick={onOpen} />;

  if (!children) {
    return (
      <div className={clsx('tw:flex tw:items-center tw:gap-2', extraClassNames)}>
        <span className="tw:cursor-pointer tw:text-sm tw:whitespace-nowrap tw:text-sq-text-color tw:dark:text-sq-text-color-dark">
          {label}
        </span>
        {swatch}
      </div>
    );
  }

  return (
    <div className={clsx('tw:flex tw:items-center tw:gap-2', extraClassNames)}>
      {triggerLabel}
      <Root>
        <Trigger asChild>{swatch}</Trigger>
        <Content align={align} className={popoverContentStyles} data-testid={testId} side={placement} sideOffset={2}>
          {children}
        </Content>
      </Root>
    </div>
  );
}

export default ColorPickerControl;
